import { useEffect, useRef, useState } from "react";
import { Cloud, CloudRain, CloudSnow, CloudLightning, Sun, CloudSun, CloudFog, Wind } from "lucide-react";
import { TV_BLUE } from "@/components/broadcast/TvFrame";
import { BACKEND_URL } from "@/lib/api";

export const SITE_DOMAIN = "georgiaweathers.com";
export const SLIDE_MS = 8000;
// --- Icon helper ---
function iconFor(condition = "", size = "h-10 w-10") {
  const c = (condition || "").toLowerCase();
  const cls = `${size} drop-shadow-[0_4px_12px_rgba(244,162,97,0.4)]`;
  if (c.includes("thunder") || c.includes("storm")) return <CloudLightning className={`${cls} text-[#ffd60a]`} />;
  if (c.includes("snow")) return <CloudSnow className={`${cls} text-sky-300`} />;
  if (c.includes("rain") || c.includes("shower")) return <CloudRain className={`${cls} text-sky-300`} />;
  if (c.includes("fog") || c.includes("mist")) return <CloudFog className={`${cls} text-gray-300`} />;
  if (c.includes("wind")) return <Wind className={`${cls} text-gray-300`} />;
  if (c.includes("partly") || c.includes("mostly")) return <CloudSun className={`${cls} text-[#ffd60a]`} />;
  if (c.includes("sun") || c.includes("clear") || c.includes("fair")) return <Sun className={`${cls} text-[#ffd60a]`} />;
  return <Cloud className={`${cls} text-gray-300`} />;
}
// --- Georgia SVG map — vector-accurate outline from the US Census cartographic
// boundary file (2010 states, 1:5m). Simplified with Shapely's Douglas-Peucker
// (tol ≈ 0.012°) down to 163 waypoints, preserving the Savannah River meanders,
// coastal hook, and Chattahoochee curves while keeping the path compact.
const GA_PATH =
  "M 59.7 51.2 L 212.5 49.9 L 211.8 54.7 L 206.6 59.2 L 204.6 59.4 L 202.8 63.7 L 201.4 63.4 " +
  "L 199.4 66.1 L 199.6 68.4 L 197.6 70.8 L 198.4 74.4 L 205.0 79.8 L 208.8 80.2 L 217.1 " +
  "89.6 L 226.7 90.2 L 229.3 98.6 L 231.8 100.6 L 234.7 106.2 L 236.5 115.1 L 241.1 119.5 L " +
  "241.0 121.1 L 244.0 124.4 L 246.3 130.8 L 248.3 130.9 L 252.5 135.7 L 260.5 140.4 L 265.3 " +
  "145.5 L 269.0 155.6 L 277.6 160.0 L 281.3 165.9 L 284.9 167.7 L 285.7 169.7 L 284.3 172.2 " +
  "L 285.0 174.5 L 283.7 174.8 L 284.1 176.8 L 289.8 179.7 L 288.8 181.1 L 290.3 182.3 L " +
  "289.5 184.2 L 292.3 187.0 L 294.6 186.5 L 295.3 188.0 L 294.3 189.4 L 296.1 192.4 L 304.1 " +
  "196.0 L 311.5 202.5 L 310.9 208.1 L 313.2 211.0 L 312.3 212.3 L 313.8 212.8 L 313.8 214.7 " +
  "L 316.0 217.1 L 315.4 219.6 L 316.5 222.7 L 315.5 226.0 L 317.6 229.9 L 316.0 231.6 L " +
  "319.2 235.6 L 324.4 237.2 L 324.7 238.8 L 330.2 244.2 L 329.1 247.3 L 334.1 256.4 L 332.0 " +
  "261.1 L 334.4 265.3 L 334.5 270.8 L 338.6 273.3 L 341.5 272.1 L 346.4 276.8 L 351.4 278.9 " +
  "L 347.9 283.7 L 344.8 283.1 L 343.3 284.3 L 343.6 286.2 L 345.6 286.7 L 342.1 290.7 L " +
  "337.6 289.2 L 337.0 292.9 L 339.4 294.1 L 336.9 298.1 L 333.6 301.0 L 329.8 300.2 L 329.2 " +
  "301.3 L 331.8 303.4 L 333.6 303.1 L 333.5 308.6 L 331.0 313.8 L 329.1 312.9 L 325.7 314.4 " +
  "L 325.8 315.9 L 329.4 315.2 L 330.8 316.8 L 324.6 328.3 L 324.3 331.4 L 326.0 332.2 L " +
  "324.0 340.2 L 320.9 342.1 L 319.1 345.9 L 317.2 346.1 L 315.9 355.1 L 314.0 355.2 L 311.4 " +
  "358.1 L 315.5 359.7 L 316.6 358.1 L 316.9 359.6 L 316.8 363.4 L 313.5 374.0 L 314.4 378.6 " +
  "L 297.6 375.9 L 292.2 372.5 L 288.5 372.3 L 286.8 370.1 L 283.5 369.6 L 282.0 373.3 L " +
  "279.0 372.6 L 279.7 374.5 L 277.7 377.1 L 277.4 382.8 L 280.1 389.8 L 278.2 404.0 L 276.7 " +
  "405.4 L 270.5 405.6 L 268.6 404.0 L 267.5 400.5 L 268.1 395.8 L 266.0 391.8 L 267.3 389.4 " +
  "L 105.0 378.5 L 102.0 375.4 L 100.7 365.4 L 97.8 361.4 L 97.8 359.0 L 96.4 358.3 L 96.8 " +
  "355.3 L 94.9 349.0 L 90.2 342.1 L 90.8 339.1 L 89.7 335.1 L 91.2 333.8 L 91.1 328.5 L " +
  "94.2 314.9 L 93.2 308.9 L 89.0 303.2 L 88.1 292.1 L 88.5 288.0 L 89.9 287.8 L 91.9 284.4 " +
  "L 91.4 282.9 L 93.4 278.8 L 93.0 269.5 L 103.4 259.8 L 96.3 254.6 L 99.0 247.3 L 97.0 " +
  "245.1 L 96.3 239.7 L 92.1 234.2 L 88.3 222.0 L 88.6 220.1 L 86.5 217.6 L 86.8 214.7 L " +
  "85.4 213.8 L 59.7 51.2 Z";
// Map real lat/lon to the 400x460 viewBox.
// lat range: 30.3 - 35.0  (height 4.7)  → y: 50 (top) to 410 (bottom)
// lon range: -85.6 - -80.7 (width 4.9) → x: 60 (west) to 360 (east)
function projectCity(lat, lon) {
  const x = 60 + ((lon + 85.6) / 4.9) * 300;
  const y = 50 + ((35.0 - lat) / 4.7) * 360;
  return { x, y };
}
// approximate lat/lon for the seeded cities (kept here to avoid an extra fetch)
const CITY_COORDS = {
  // Central & North
  atlanta:    { lat: 33.749, lon: -84.388 },
  athens:     { lat: 33.960, lon: -83.378 },
  rome:       { lat: 34.257, lon: -85.164 },
  augusta:    { lat: 33.474, lon: -82.011 },
  macon:      { lat: 32.841, lon: -83.633 },
  columbus:   { lat: 32.461, lon: -84.988 },
  // Southern half
  statesboro: { lat: 32.448, lon: -81.783 },
  albany:     { lat: 31.578, lon: -84.156 },
  tifton:     { lat: 31.450, lon: -83.508 },
  thomasville: { lat: 30.837, lon: -84.10 },
  savannah:   { lat: 32.081, lon: -81.091 },
  brunswick:  { lat: 31.150, lon: -81.492 },
  waycross:   { lat: 31.213, lon: -82.354 },
  valdosta:   { lat: 30.833, lon: -83.18 },
};
const GA_ICON_PATH = "M6.8 0.3 L52.1 0.0 L47.9 6.7 L53.4 11.1 L56.3 11.4 L62.0 22.5 L66.7 25.8 L68.8 29.6 L72.8 32.8 L74.9 37.8 L81.3 42.9 L82.9 51.2 L86.7 54.6 L88.3 62.1 L93.2 64.1 L88.0 70.7 L85.1 81.6 L83.1 83.8 L82.3 92.4 L72.0 90.8 L71.3 99.9 L69.2 100.0 L67.7 95.4 L20.3 92.4 L15.9 82.2 L17.0 74.4 L15.2 69.3 L16.9 61.6 L19.4 58.9 L17.7 57.6 L17.8 53.6 L14.5 45.9 Z";
const BOLT_PATH = "M57 18 36 58h17l-4 30 24-44H56l5-26z";
const BRIGHT_BG = "linear-gradient(135deg, #f4f6fa 0%, #dfe6ef 100%)";
const HATCH = "repeating-linear-gradient(135deg, #000 0 2px, transparent 2px 26px)";
function BrightStage({ children, testid, className = "" }) {
  return (
    <div data-testid={testid} className={`relative h-full w-full overflow-hidden ${className}`} style={{ background: BRIGHT_BG }}>
      <div className="absolute inset-0 opacity-[0.08]" style={{ backgroundImage: HATCH }} />
      <div className="relative h-full w-full">{children}</div>
    </div>
  );
}
function Pill({ children, color = TV_BLUE, testid }) {
  return <div data-testid={testid} className="inline-flex w-fit items-center gap-3 rounded-full px-5 py-2 font-oswald text-[20px] font-bold uppercase tracking-[0.3em] text-white" style={{ background: color }}>{children}</div>;
}
// --- Model-guided period helpers ---
function tonightPeriod(city) {
  return (city?.forecast_periods || []).find((p) => p.isDaytime === false) || null;
}

function tomorrowPeriod(city) {
  const periods = city?.forecast_periods || [];
  const nightIdx = periods.findIndex((p) => p.isDaytime === false);
  if (nightIdx < 0) return periods.find((p) => p.isDaytime === true) || null;
  return periods.slice(nightIdx + 1).find((p) => p.isDaytime === true) || null;
}
function precipPercent(p) {
  const v = p?.probabilityOfPrecipitation?.value;
  return typeof v === "number" && v > 0 ? v : null;
}
function useCycle(len, perMs) {
  const [k, setK] = useState(0);
  useEffect(() => {
    if (len < 2) return undefined;
    const t = setInterval(() => setK((n) => (n + 1) % len), Math.max(3000, perMs));
    return () => clearInterval(t);
  }, [len, perMs]);
  return Math.min(k, Math.max(len - 1, 0));
}
// -------------------- Reusable Georgia value map --------------------
const toPoints = (cities, valueKey) => cities.map((c) => ({ id: c.id, name: c.name, value: c[valueKey], ...(CITY_COORDS[c.id] || {}) })).filter((p) => p.lat != null);
const _CAM_DEAD_TTL_MS = 8 * 60 * 1000;
const _deadCams = new Map(); // camId -> expiry timestamp (ms)
function _isCamDead(id) {
  const until = _deadCams.get(id);
  if (!until) return false;
  if (Date.now() >= until) { _deadCams.delete(id); return false; }
  return true;
}
function _markCamDead(id) {
  _deadCams.set(id, Date.now() + _CAM_DEAD_TTL_MS);
}
const _warmed = new Set();
function warmForecastVideos(list) {
  if (typeof document === "undefined" || !document.documentElement.classList.contains("encoder-lite")) return;
  (list || []).forEach((v) => {
    if (!v?.url || _warmed.has(v.id)) return;
    _warmed.add(v.id);
    fetch(`${BACKEND_URL}${v.url}`, { cache: "force-cache" }).then((r) => r.arrayBuffer()).catch(() => _warmed.delete(v.id));
  });
}

// Count-up animation: eases a number from `start` to `value` over `duration`ms (optional `delay`).
// rAF + opacity-free (encoder-safe). Returns null until a finite value is available.
function useCountUp(value, { duration = 700, delay = 0, start = 0 } = {}) {
  const target = Number(value);
  const valid = Number.isFinite(target);
  const [display, setDisplay] = useState(valid ? start : null);
  const fromRef = useRef(valid ? start : 0);
  useEffect(() => {
    if (!valid) { setDisplay(null); return undefined; }
    const from = fromRef.current;
    if (from === target) { setDisplay(target); return undefined; }
    let raf = 0;
    const ease = (t) => 1 - Math.pow(1 - t, 3);
    const run = () => {
      const startT = performance.now();
      const step = (now) => {
        const p = Math.min(1, (now - startT) / duration);
        setDisplay(from + (target - from) * ease(p));
        if (p < 1) raf = requestAnimationFrame(step);
        else fromRef.current = target;
      };
      raf = requestAnimationFrame(step);
    };
    const t = delay > 0 ? setTimeout(run, delay) : null;
    if (!t) run();
    return () => { if (t) clearTimeout(t); cancelAnimationFrame(raf); };
  }, [target, valid, duration, delay]);
  return display;
}

// Rolling number span — temps / scores roll into place instead of popping in.
function RollingNumber({ value, duration, delay, start, decimals = 0, suffix = "", className, testId }) {
  const n = useCountUp(value, { duration, delay, start });
  if (n == null) return <span className={className} data-testid={testId}>—{suffix}</span>;
  return <span className={className} data-testid={testId}>{n.toFixed(decimals)}{suffix}</span>;
}
export {
  iconFor, GA_PATH, projectCity, CITY_COORDS, GA_ICON_PATH, BOLT_PATH,
  BRIGHT_BG, HATCH, BrightStage, Pill,
  tonightPeriod, tomorrowPeriod, precipPercent, useCycle, toPoints,
  _isCamDead, _markCamDead, warmForecastVideos, useCountUp, RollingNumber,
};
